import type { ReactNode } from 'react'
import { useNavigate } from 'react-router'
import { ArrowLeft } from 'lucide-react'
import { getPropertyDisplayName } from '@/lib/format-utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PropertyPicker } from '@/components/property/property-picker'
import { usePropertyOptions } from '@/hooks/use-property-options'

type PropertyLikeArg = Parameters<typeof getPropertyDisplayName>[0]

interface PropertyHeaderProps {
  propertyId: string
  /** `GET /admin/properties/:id`'s body, when it has loaded. */
  property?: { archived?: boolean | null } | null
  /** True while that request is still in flight. */
  isPropertyLoading?: boolean
  /** The page's own controls, on the right of the same row. */
  actions?: ReactNode
}

/**
 * The one header every property route shows (CAP-1034): back to the grid, the
 * property name as a picker to jump to another property, the Archived badge
 * when it applies, and the page's actions on the right. No ID, no photo; the
 * page title stays "Properties" in the top bar.
 *
 * While the property itself loads (or if it failed), the name comes from the
 * cached property list, so the header and its picker never wait on the page.
 */
export function PropertyHeader({
  propertyId,
  property,
  isPropertyLoading = false,
  actions,
}: PropertyHeaderProps) {
  const navigate = useNavigate()
  const list = usePropertyOptions()
  const { options, isLoading: isListLoading } = list
  const listed = options.find(option => option.id === propertyId)

  const name = property
    ? getPropertyDisplayName(property as PropertyLikeArg, propertyId)
    : (listed?.name ?? (isPropertyLoading || isListLoading ? null : 'Unknown property'))
  const archived = property ? property.archived === true : listed?.archived === true

  return (
    <div
      data-testid="property-header"
      className="mb-4 flex min-h-10 flex-wrap items-center justify-between gap-x-3 gap-y-2"
    >
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-textMuted"
          onClick={() => navigate('/properties')}
          aria-label="Back to properties"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PropertyPicker propertyId={propertyId} name={name} list={list} />
        {archived && (
          <Badge variant="secondary" className="shrink-0">
            Archived
          </Badge>
        )}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}
